import {
  Box,
  Chip,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState, useMemo, useEffect } from "react";
import ReusableDataTable from "../../Component/DataTable/ReusableDataTablePrime";
import { FilterMatchMode } from "primereact/api";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../store/CommonSlice";
import { CustomMessage } from "../../const/Spinner";
import { enqueueSnackbar } from "notistack";
import ReusablePrimeButton from "../../Component/Buttons/ReusablePrimeButton";
import AddIcon from "@mui/icons-material/Add";
import { getTransporterColumns } from "../../const/AssertData";
import { TransporterMaster } from "../../types/assetTypes";
import ReusablePrimeReactDialog from "../../Component/Dialog/ReusablePrimeReactDialog";
import { getCountryAssetMasterDataList, getRegionMasterDataList, getTransporterById, getTransporterMasterDataList, saveTransporter } from "../../api/Services/masterServie";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { PAGESTART, PAGESEND } from "../../api/endpoint";
import { RootState } from "../../store/Reduxstore";

interface AssetTransporterProps {
  setPathname: (path: string) => void;
}

const Transporter: React.FC = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<TransporterMaster[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    rows: 13,
    totalRecords: 0,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [assetTransporterData, setAssetTransporterData] = useState<TransporterMaster>(
    {
      transporterId: 0,
      transporterName: "",
      contactName: "",
      email: "",
      mobileWithCountryCode: "",
      phone: 0,
      comments: "",
      countryIds: [],  // Assuming multiple countries can be selected
      regionIds: []
    }
  );

  const [regionOptions, setRegionOptions] = useState<{ id: number; name: string }[]>([]);
  const [countryOptions, setCountryOptions] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await getCountryAssetMasterDataList(PAGESTART, PAGESEND, {}); // Provide default values
        if (response.status.code === 200) {
          console.log("Fetched countries:", response.data);
          setCountryOptions(response.data.map(countries => ({
            id: countries.currentLocationId,
            name: countries.countryName,
          })));



        } else {
          setCountryOptions([]);
          CustomMessage(response.status.message, "error", enqueueSnackbar);
        }
      } catch (error: any) {
        CustomMessage(error.message, "error", enqueueSnackbar);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await getRegionMasterDataList(PAGESTART, PAGESEND, {}); // Provide default values
        if (response.status.code === 200) {
          setRegionOptions(response.data.map(regions => ({
            id: regions.regionId,
            name: regions.regionName,
          })));

        } else {
          setRegionOptions([]);
          CustomMessage(response.status.message, "error", enqueueSnackbar);
        }
      } catch (error: any) {
        CustomMessage(error.message, "error", enqueueSnackbar);
      }
    };

    fetchRegions();
  }, []);


  // Memoized column definition
  const columns = useMemo(() => getTransporterColumns(), []);

  if (columns.filter((val) => val.field === "action").length === 0) {
    columns.push({
      field: "action",
      header: "Action",
      sortable: false,
      filter: false,
      filterField: "action",
      filterPlaceholder: "Search",
      style: { minWidth: "12rem" },
      body: (rowData: any) => (
        <ReusablePrimeButton
          icon="pi pi-pencil"
          className="p-button-rounded p-button-outlined"
          onClick={() => handleEditFamilyAsset(rowData.transporterId)}
        />
      ),
    });
  }

  const [filterData, setFilterData] = useState<Record<string, any>>(() =>
    (columns ?? []).reduce((acc, col) => {
      if (col.field) {
        acc[col.field] = { value: "", matchMode: "startsWith" }; // Ensure correct structure
      }
      return acc;
    }, {} as Record<string, { value: string; matchMode: string }>),
  );

  const handleEditFamilyAsset = async (transporterId: number) => {
    dispatch(setLoading(true));
    try {
      const response = await getTransporterById(transporterId);
      let { data, status } = response;
      if (status.code === 200 && data) {
        setAssetTransporterData(data);
        setOpenDialog(true);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Generate filters dynamically

  const onFetchData = async (page: number, rows: number, filter?: Record<string, any>) => {
    dispatch(setLoading(true));
    try {
      const { data, status, pagination } =
        await getTransporterMasterDataList(page, rows, filter ?? filterData);
      if (status.code === 200 && data?.length > 0) {
        setData(data);
        setPagination({
          page: pagination.currentPage,
          rows: pagination.limit,
          totalRecords: pagination.totalCount,
        });
      } else {
        setData([]);
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    setAssetTransporterData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClear = () => {
    setOpenDialog(false);
    setAssetTransporterData({
      transporterId: 0,
      transporterName: "",
      contactName: "",
      email: "",
      mobileWithCountryCode: "",
      phone: 0,
      comments: "",
      countryIds: [],  // Assuming multiple countries can be selected
      regionIds: []
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event?.preventDefault();
    if (assetTransporterData.transporterName === "") {
      CustomMessage("Add the name", "error", enqueueSnackbar);
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await saveTransporter(assetTransporterData);
      let { status } = response;
      if (status.code === 200 || status.code === 201) {

        handleClear();
        onFetchData(PAGESTART, PAGESEND, filterData);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };
  const userDetails = useSelector((state: RootState) => state.auth)

  return (
    <>
      <div
        // className="mb-0 add-absolute"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          //   top: "-37px",
          //   zIndex: "1",
        }}
      >
        {userDetails.role.some(
          r => r.roleName === "Admin" || r.roleName === "Logistics List Write"
        ) && (
            <Chip
              className="btn-black-outlined mb-3"
              label="New"
              color="warning"
              onClick={() => setOpenDialog(true)}
              icon={<AddIcon />}
              variant="outlined"
            />
          )}
      </div>
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          borderRadius: 3,
          boxShadow: 3,
          padding: 2,
        }}
      >
        <ReusableDataTable
          data={data}
          columns={columns}
          onFetchData={onFetchData}
          emptyMessage="No data found."
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          selectionMode="multiple"
          rowsPerPageOptions={[5, 10, 25, 50]}
          pagination={pagination}
          setPagination={setPagination}
          filters={filterData}
          setFilters={setFilterData}
          onRowDoubleClick={(rowData) => {
            console.log('Double-click row:', rowData);
            handleEditFamilyAsset(rowData.transporterId);
          }}
        />
      </Box>

      <ReusablePrimeReactDialog
        header="Add Transporter"
        visible={openDialog}
        onHide={handleClear}
        style={{
          minHeight: '400px',         // Minimum height of the dialog
          maxHeight: '80vh',          // Prevent it from growing too tall
          overflow: 'hidden'          // Let content area handle scroll
        }}
        contentStyle={{
          maxHeight: '60vh',          // Controls scrolling area height
          overflowY: 'auto',          // Enables vertical scroll
          paddingRight: '8px',        // Prevents scroll bar from overlapping content
        }}
      >
        <form>
          {/* Country Name Input */}
          <div className="mb-3">
            <h6>Transporter Name</h6>
            <TextField
              fullWidth
              variant="outlined"
              value={assetTransporterData.transporterName}
              name="transporterName"
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-3">
            <h6>Contact Name</h6>
            <TextField
              fullWidth
              variant="outlined"
              value={assetTransporterData.contactName}
              name="contactName"
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-3">
            <h6>Email</h6>
            <TextField
              fullWidth
              variant="outlined"
              type="email"
              value={assetTransporterData.email}
              name="email"
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-3">
            <h6>Mobile with Country Code</h6>

            <TextField
              fullWidth
              variant="outlined"
              value={assetTransporterData.mobileWithCountryCode}
              name="mobileWithCountryCode"
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-3">
            <h6>Phone</h6>
            <TextField
              fullWidth
              variant="outlined"

              value={assetTransporterData.phone}
              name="phone"
              onChange={handleChange}
            />
          </div>

          <Box sx={{ mt: 2, mb: 1, fontWeight: "bold", position: "relative" }}>
            <Typography variant="body1" sx={{ mt: 2, mb: 0, fontWeight: "bold" }}>
              Select Countires
            </Typography>
            <MultiSelect
              value={assetTransporterData.countryIds}
              options={countryOptions}
              optionLabel="name"
              optionValue="id"
              placeholder="Select Countries"
              style={{ width: "100%", marginTop: "4px" }}
              appendTo="self"
              filter
              onChange={(e: MultiSelectChangeEvent) => {
                console.log("Selected countries:", e.value);
                setAssetTransporterData((prev) => ({
                  ...prev,
                  countryIds: e.value as number[],
                }));
              }}

            />

          </Box>

          <Box sx={{ mt: 2, mb: 1, fontWeight: "bold", position: "relative" }}>
            <Typography variant="body1" sx={{ mt: 2, mb: 0, fontWeight: "bold" }}>
              Select Regions
            </Typography>
            <MultiSelect
              value={assetTransporterData.regionIds}
              options={regionOptions}
              optionLabel="name"
              optionValue="id"
              placeholder="Select Regions"
              style={{ width: "100%", marginTop: "4px" }}
              appendTo="self"
              filter // 🔍 Enable search filter
              onChange={(e: MultiSelectChangeEvent) =>
                setAssetTransporterData((prev) => ({
                  ...prev,
                  regionIds: e.value as number[],
                }))
              }
            />
          </Box>

          <div className="mb-3">
            <h6>Comments</h6>
            <TextField
              fullWidth
              variant="outlined"

              value={assetTransporterData.comments}
              name="comments"
              onChange={handleChange}
            />

          </div>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 4,
            }}
          >
            <ReusablePrimeButton
              onClick={handleClear}
              severity="contrast"
              title="Cancel"

            >
              Cancel
            </ReusablePrimeButton>
            <ReusablePrimeButton severity="info" onClick={handleSubmit} allowedRoles={["Admin", "Logistics List Write"]}>
              Submit
            </ReusablePrimeButton>
          </Box>
        </form>

      </ReusablePrimeReactDialog>

    </>
  );
};

export default Transporter;


